import { useState } from 'react';
import Button from 'src/components/buttons/button';
import NumberedList from 'src/components/numberedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">Why is exercise good for you?</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">How much physical activity should I do?</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">Top 4 tips to get moving</a></div></li>
      <li><div className="navigator__contents__number">4.</div><div className="navigator__contents__content-text"><a href="#4">Get inspired - Walk Good Nottingham</a></div></li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">
        {'Week 7: Being more physically active'}
      </h1>
      <p>Being more active in your day-to-day routine will help you bring your blood pressure to a healthier level.</p>
      <div className="visible-xs">{getMenu()}</div>

      <h2>Why physical activity is good for you</h2>
      <p>{`We all know that exercise is good for us, but it's especially important if you have high blood pressure. Research shows that regular exercise can help lower blood pressure and cut down your risk of heart disease. But what actually happens inside your body when you get active? Watch our animation to find out.`}</p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/wWGulLAa0O0"
        />
      </div>
      <p>
        Exercise will make your heart muscle stronger over time.
        The stronger your heart, the less effort it needs to put into pumping your blood.
        Once your heart is working less hard, the strain on your blood vessels decreases and your blood pressure goes down.
      </p>

      <h2>How much physical activity should I do?</h2>
      <p>{`Look for things you can do that make you breathe harder, feel warmer and your heart beat faster. You don't have to be flat out - you should still be able to hold a conversation.`}</p>
      <p><strong>Aim to be active for about half an hour a day, 5 days a week at least.</strong></p>
      <p>{`You'd be surprised how many activities count as part of your 30 minutes a day. Walking, swimming, gardening and climbing the stairs are all heart-healthy activities.`}</p>
      <a href="https://www.bhf.org.uk/heart-health/preventing-heart-disease/staying-active/getting-active" rel="noreferrer" target="_blank">
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Start getting active"
        />
      </a>

      <h2>Top 4 tips to get moving</h2>
      <section className="navigator__section-half">
        <p>
          Getting moving is something you need to do now. If you put your energy into planning what you will do in the future, it can stop you starting today.
          Focus on making each day a little bit better than the day before. Keep looking for ways to fit an extra few minutes of movement into your day.
        </p>
      </section>
      <img alt="A person walking" className="navigator__image-half" src={`${PROGRAM_URL}wk7-exercise.jpg`} />
      <p>Some days will feel harder some than others. These tips will help you stay focused.</p>
      <NumberedList items={[
        'Start small. Can you walk to work? Get off the bus a stop early? Walk up and down the stairs a few times? Every little counts. ',
        `Keep track of your progress. Whether it's keeping a diary, using a pedometer, a fitness tracker, or a smartphone app, seeing how far you've come will keep you going.`,
        `Change it up. Try a variety of activities so that you're always pushing yourself and not getting bored. Walk a different route, do 5 minutes more each time or invite a friend along so you stay interested.`,
        `Build activity into your routine. When activity becomes part of your day-to-day routine, like walking to work, or the supermarket, it's much easier to stick with it.`]
      }
      />
      <a href="https://www.bhf.org.uk/heart-health/preventing-heart-disease/staying-active" rel="noreferrer" target="_blank">
        Read more tips for staying active here {'>'}
      </a>

      <h2>Get inspired - Walk Good Nottingham</h2>
      <p>
        This group of friends set up their own walking group to protect their health, explore their city and be there for each other.
        See how they did it and get inspired.
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/7TiagQ_jyb4"
        />
      </div>

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Join the conversation on HealthUnlocked
        </div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice and encouragement.`}
          {' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
